import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Modal,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  CopyOutlined,
  DownloadOutlined,
  EyeOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  useGetPhdDocumentTemplatesQuery,
  useLazyGetPhdDocumentTemplateQuery,
  useLazyPreviewPhdDocumentTemplateQuery,
  useSavePhdDocumentTemplateMutation,
} from '../../../../../../redux/services/phd.jsx';
import { useToastMessages } from '../../../../../../hooks/useToastMessages.jsx';
import { downloadBlobResponse } from '../../../../../../utils/helpers/downloadBlobResponse';
import { Spinner } from '../../../../../common';
import PhdSettingsSection from './PhdSettingsSection.jsx';
import PhdHelpHint from '../../../../../common/Phd/PhdHelpHint.jsx';

const PhdDocumentTemplatesSection = ({ onCreateVariable }) => {
  const { t } = useTranslation();
  const { data: templates, isLoading } = useGetPhdDocumentTemplatesQuery();
  const [downloadTemplate] = useLazyGetPhdDocumentTemplateQuery();
  const [previewTemplate] = useLazyPreviewPhdDocumentTemplateQuery();
  const [saveTemplate] = useSavePhdDocumentTemplateMutation();
  const { saveSuccess, saveFailed } = useToastMessages();
  const [busyKey, setBusyKey] = useState(null);

  const rows = useMemo(
    () => (Array.isArray(templates) ? templates : []),
    [templates],
  );

  const handleDownload = async (record) => {
    setBusyKey(`download:${record.key}`);
    try {
      const response = await downloadTemplate(record.key).unwrap();
      downloadBlobResponse(response, record.template);
    } catch (error) {
      saveFailed(error);
    } finally {
      setBusyKey(null);
    }
  };

  const handleCopyToken = async (token) => {
    try {
      await navigator.clipboard.writeText(token);
      message.success(t('Skopírované: {{token}}', { token }));
    } catch (error) {
      message.info(token);
    }
  };

  const handlePreview = async (record) => {
    setBusyKey(`preview:${record.key}`);
    try {
      const response = await previewTemplate(record.key).unwrap();
      downloadBlobResponse(response, `${record.key}-ukazka.docx`);
    } catch (error) {
      saveFailed(error);
    } finally {
      setBusyKey(null);
    }
  };

  const notifyUnknownPlaceholders = (unknown) => {
    if (!Array.isArray(unknown) || unknown.length === 0) {
      return;
    }

    Modal.info({
      title: t('Šablóna obsahuje neznáme premenné'),
      content: (
        <div>
          <p>
            {t(
              'Tieto premenné nemajú priradenú hodnotu a v dokumente ostanú prázdne. Môžete si ich vytvoriť ako vlastné premenné.',
            )}
          </p>
          <Space wrap>
            {unknown.map((item) => (
              <Tag key={item} color="blue">{`\${${item}}`}</Tag>
            ))}
          </Space>
        </div>
      ),
      okText: onCreateVariable ? t('Vytvoriť premennú') : t('Rozumiem'),
      onOk: () => onCreateVariable?.(unknown[0]),
    });
  };

  const handleUpload = async (record, file) => {
    setBusyKey(`upload:${record.key}`);
    try {
      const metadata = await saveTemplate({ key: record.key, file }).unwrap();
      saveSuccess();
      notifyUnknownPlaceholders(metadata?.unknown_placeholders);
    } catch (error) {
      saveFailed(error);
    } finally {
      setBusyKey(null);
    }
  };

  const columns = [
    {
      title: t('Dokument'),
      dataIndex: 'label',
      key: 'label',
      render: (value, record) => (
        <Space orientation="vertical" size={2}>
          <Typography.Text strong>{value}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {record.template}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: (
        <span>
          {t('Stav')}
          <PhdHelpHint
            label={t('Čo znamená stav šablóny?')}
            title={t(
              'Dostupná šablóna sa môže použiť na export. Chýbajúcu šablónu treba nahrať pred generovaním dokumentu.',
            )}
          />
        </span>
      ),
      dataIndex: 'exists',
      key: 'exists',
      width: 120,
      render: (value) =>
        value ? (
          <Tag color="green">{t('Dostupná')}</Tag>
        ) : (
          <Tag color="red">{t('Chýba')}</Tag>
        ),
    },
    {
      title: t('Upravené'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: (value) =>
        value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—',
    },
    {
      title: (
        <span>
          {t('Premenné')}
          <PhdHelpHint
            label={t('Čo znamenajú premenné šablóny?')}
            title={t(
              'Systémové a vlastné premenné sa pri exporte nahradia hodnotami. Neznáme premenné zostanú v dokumente prázdne.',
            )}
          />
        </span>
      ),
      dataIndex: 'placeholders',
      key: 'placeholders',
      render: (value, record) => {
        const placeholders = Array.isArray(value) ? value : [];
        const unused = Array.isArray(record.missing_placeholders)
          ? record.missing_placeholders
          : [];
        const custom = Array.isArray(record.custom_placeholders)
          ? record.custom_placeholders
          : [];
        const unknown = Array.isArray(record.unknown_placeholders)
          ? record.unknown_placeholders
          : [];
        if (
          placeholders.length === 0 &&
          custom.length === 0 &&
          unknown.length === 0
        ) {
          return '—';
        }

        return (
          <Space orientation="vertical" size={2}>
            <Tooltip
              title={placeholders.map((item) => `\${${item}}`).join(', ')}
            >
              <Typography.Text type="secondary">
                {t('{{count}} premenných', { count: placeholders.length })}
              </Typography.Text>
            </Tooltip>
            {unused.length > 0 ? (
              <Tooltip title={unused.map((item) => `\${${item}}`).join(', ')}>
                <Tag color="gold">
                  {t('{{count}} nepoužité', { count: unused.length })}
                </Tag>
              </Tooltip>
            ) : null}
            {custom.length > 0 ? (
              <Tooltip title={custom.map((item) => `\${${item}}`).join(', ')}>
                <Tag color="green">
                  {t('{{count}} vlastné', { count: custom.length })}
                </Tag>
              </Tooltip>
            ) : null}
            {unknown.length > 0 ? (
              <Tooltip title={unknown.map((item) => `\${${item}}`).join(', ')}>
                <Tag color="red">
                  {t('{{count}} neznáme', { count: unknown.length })}
                </Tag>
              </Tooltip>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: t('Akcie'),
      key: 'actions',
      width: 320,
      render: (_, record) => (
        <Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(record)}
            loading={busyKey === `download:${record.key}`}
            disabled={!record.exists}
          >
            {t('Stiahnuť')}
          </Button>
          <Button
            icon={<EyeOutlined />}
            onClick={() => handlePreview(record)}
            loading={busyKey === `preview:${record.key}`}
            disabled={!record.exists}
          >
            {t('Vygenerovať ukážku')}
          </Button>
          <Upload
            accept=".docx"
            showUploadList={false}
            beforeUpload={(file) => {
              handleUpload(record, file);
              return false;
            }}
          >
            <Button
              icon={<UploadOutlined />}
              loading={busyKey === `upload:${record.key}`}
            >
              {t('Nahradiť')}
            </Button>
          </Upload>
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return <Spinner />;
  }

  const expandedRowRender = (record) => {
    const details = Array.isArray(record.placeholder_details)
      ? record.placeholder_details
      : [];

    return (
      <Table
        rowKey="key"
        dataSource={details}
        pagination={false}
        size="small"
        scroll={{ x: 'max-content' }}
        columns={[
          {
            title: t('Premenná'),
            dataIndex: 'token',
            key: 'token',
            width: 360,
            render: (value) => (
              <Space size={4} style={{ whiteSpace: 'nowrap' }}>
                <Typography.Text code>{value}</Typography.Text>
                <Tooltip title={t('Kopírovať premennú')}>
                  <Button
                    size="small"
                    type="text"
                    icon={<CopyOutlined />}
                    onClick={() => handleCopyToken(value)}
                  />
                </Tooltip>
              </Space>
            ),
          },
          {
            title: t('Význam'),
            dataIndex: 'description',
            key: 'description',
            render: (value) => value || '—',
          },
          {
            title: t('V šablóne'),
            dataIndex: 'used',
            key: 'used',
            width: 130,
            render: (value) =>
              value ? (
                <Tag color="green">{t('Použitá')}</Tag>
              ) : (
                <Tag>{t('Dostupná')}</Tag>
              ),
          },
        ]}
      />
    );
  };

  return (
    <PhdSettingsSection
      title={t('Šablóny generovaných dokumentov')}
      description={t(
        'Každý dokument používa DOCX šablónu s premennými vo formáte ${premenná}. Rozbalením riadku zobrazíte dostupné premenné a ich význam. Nahratie novej šablóny automaticky nahradí aktívnu verziu a pôvodný súbor sa uloží ako záloha.',
      )}
    >
      <Table
        rowKey="key"
        dataSource={rows}
        columns={columns}
        pagination={false}
        size="small"
        expandable={{ expandedRowRender }}
      />
    </PhdSettingsSection>
  );
};

PhdDocumentTemplatesSection.propTypes = {
  onCreateVariable: PropTypes.func,
};

export default PhdDocumentTemplatesSection;
